import { GuidePlacement } from './engagement.models';

export interface Box {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface Size {
    width: number;
    height: number;
}

const GAP = 12;
const EDGE = 8;
/** Matches the medium breakpoint, below which the card is a bottom sheet. */
export const NARROW_VIEWPORT = 600;

/**
 * Where to put the guide card relative to the target: the preferred side if the card fits there,
 * else the side with the most room, clamped inside the viewport. Narrow screens only use the
 * space above or below, where there is no room beside the target.
 */
export function computeCardPosition(
    target: Box,
    card: Size,
    viewport: Size,
    placement: GuidePlacement = 'auto'
): { top: number; left: number } {
    const room = {
        top: target.top - GAP,
        bottom: viewport.height - (target.top + target.height) - GAP,
        left: target.left - GAP,
        right: viewport.width - (target.left + target.width) - GAP
    };
    const narrow = viewport.width < NARROW_VIEWPORT;
    const sides = (narrow ? ['bottom', 'top'] : ['bottom', 'top', 'right', 'left']) as
        ('top' | 'bottom' | 'left' | 'right')[];
    const fits = (side: 'top' | 'bottom' | 'left' | 'right') =>
        room[side] >= (side === 'top' || side === 'bottom' ? card.height : card.width);

    const preferred = placement !== 'auto' && sides.includes(placement) ? placement : null;
    const side = preferred && fits(preferred)
        ? preferred
        : sides.find(fits) ?? sides.reduce((a, b) => (room[a] >= room[b] ? a : b));

    let top: number;
    let left: number;
    if (side === 'top' || side === 'bottom') {
        top = side === 'top' ? target.top - GAP - card.height : target.top + target.height + GAP;
        left = target.left + target.width / 2 - card.width / 2;
    } else {
        left = side === 'left' ? target.left - GAP - card.width : target.left + target.width + GAP;
        top = target.top + target.height / 2 - card.height / 2;
    }

    return {
        top: clamp(top, EDGE, viewport.height - card.height - EDGE),
        left: clamp(left, EDGE, viewport.width - card.width - EDGE)
    };
}

function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(value, Math.max(min, max)));
}
